import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import MobileHeader from '../../components/feature/MobileHeader';
import { apiService } from '../../services/api';

interface Draw {
  id: string;
  lottery_type: string;
  draw_number: string;
  draw_date: string;
  drawn_numbers: number[];
  prize_breakdown?: any;
  extra?: any;
}

const lotteryNames = {
  'mega-sena': 'Mega-Sena',
  'quina': 'Quina',
  'lotofacil': 'Lotofácil',
  'lotomania': 'Lotomania',
  'timemania': 'Timemania',
  'dia-de-sorte': 'Dia de Sorte',
  'super-sete': 'Super Sete',
  'dupla-sena': 'Dupla Sena',
  'federal': 'Loteria Federal'
};

export default function Resultados() {
  const [draws, setDraws] = useState<Draw[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncInfo, setSyncInfo] = useState<{ imported: number; checked_tickets?: number } | null>(null);
  const [selectedDraw, setSelectedDraw] = useState<Draw | null>(null);
  const [syncMessage, setSyncMessage] = useState<string>('');
  const [apiPreview, setApiPreview] = useState<{ source: string; raw: any; normalized: any } | null>(null);
  const [filter, setFilter] = useState({
    lottery_type: '',
    draw_number: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    loadDraws();
  }, [filter]);

  const loadDraws = async () => {
    try {
      setLoading(true);
      const response = await apiService.getResults(filter);
      const arr = (response.data || []).slice().sort((a: any, b: any) => {
        const ad = new Date(a.draw_date).getTime();
        const bd = new Date(b.draw_date).getTime();
        return bd - ad;
      });
      setDraws(arr);
    } catch (error) {
      console.error('Erro ao carregar resultados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncMessage(filter.lottery_type ? `Sincronizando ${lotteryNames[filter.lottery_type as keyof typeof lotteryNames] || filter.lottery_type}...` : 'Sincronizando todas as loterias...');
      const r = await apiService.syncDraws(filter.lottery_type || 'all');
      setSyncInfo(r as any);
      await loadDraws();
    } catch (error) {
      console.error('Erro ao sincronizar resultados:', error);
      setSyncInfo({ imported: 0 });
    } finally {
      setSyncing(false);
      setSyncMessage('');
    }
  };

  // Sincronização somente via ação do usuário no botão

  const handleCheckGames = async (draw: Draw) => {
    try {
      const r = await apiService.checkDrawGames(draw.id);
      alert(`Conferidos ${r.checked} jogos. Vencedores: ${r.winners}`);
    } catch (error) {
      console.error('Erro ao conferir jogos:', error);
      alert('Falha ao conferir jogos');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <Sidebar />
      <MobileHeader />
      
      <div className="md:ml-72 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold mb-2">Resultados</h1>
            <p className="text-white/60 text-sm">Confira os resultados dos sorteios e concursos</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSync}
              disabled={syncing}
              className={`px-6 py-3 rounded-xl transition-all whitespace-nowrap cursor-pointer ${syncing ? 'bg-white/10 border border-white/20 text-white/60' : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg'}`}
            >
              <i className={`${syncing ? 'ri-refresh-line animate-spin' : 'ri-refresh-line'} mr-2`}></i>
              {syncing ? 'Sincronizando...' : 'Sincronizar Agora'}
            </button>
            {/* botão "Ver retorno da API" removido */}
          </div>
        </div>
        {syncing ? (
          <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center">
            <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl p-6 text-center">
              <i className="ri-loader-4-line text-3xl animate-spin text-emerald-400 mb-3"></i>
              <p className="text-white mb-1 font-medium">{syncMessage || 'Sincronizando resultados...'}</p>
              <p className="text-white/60 text-xs">Isso pode levar até 30 segundos. Não feche a página.</p>
              <div className="mt-4 w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-2 bg-emerald-500 animate-progress"></div>
              </div>
            </div>
          </div>
        ) : syncInfo ? (
          <div className="mb-6 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm">
            Importados {syncInfo.imported} concursos {typeof syncInfo.checked_tickets !== 'undefined' ? `• Conferidos ${syncInfo.checked_tickets} tickets` : ''}
          </div>
        ) : null}

        {/* Filtros */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Loteria</label>
              <select
                value={filter.lottery_type}
                onChange={(e) => setFilter({...filter, lottery_type: e.target.value})}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">Todas</option>
                {Object.entries(lotteryNames).map(([key, name]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Concurso</label>
              <input
                type="text"
                placeholder="Número do concurso"
                value={filter.draw_number}
                onChange={(e) => setFilter({...filter, draw_number: e.target.value})}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">De</label>
              <input
                type="date"
                value={filter.date_from}
                onChange={(e) => setFilter({...filter, date_from: e.target.value})}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Até</label>
              <input
                type="date"
                value={filter.date_to}
                onChange={(e) => setFilter({...filter, date_to: e.target.value})}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
        </div>

        {/* Lista de Resultados */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-cyan-400 animate-spin mb-4"></i>
              <p className="text-white/60">Carregando resultados...</p>
            </div>
          </div>
        ) : draws.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-clover-line text-6xl text-white/20 mb-4"></i>
            <h3 className="text-xl font-bold mb-2">Nenhum resultado encontrado</h3>
            <p className="text-white/60 mb-6">Clique em "Sincronizar Agora" para buscar os resultados mais recentes.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {draws.map((draw) => (
              <div key={draw.id} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 text-xs font-semibold bg-white/10 border border-white/20 rounded-md">
                        {lotteryNames[draw.lottery_type as keyof typeof lotteryNames] || draw.lottery_type}
                      </span>
                      <span className="text-white/60 text-xs">Concurso {String((draw as any).contest_number ?? draw.draw_number ?? '').padStart(4, '0')}</span>
                    </div>
                  </div>
                  <span className="text-white/60 text-sm">{formatDate(draw.draw_date)}</span>
                </div>

                {/* Números Sorteados */}
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Números Sorteados:</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(draw.drawn_numbers ?? draw.numbers ?? []).map((num: number, idx: number) => (
                      <div key={idx} className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg text-sm font-bold">
                        {num.toString().padStart(2, '0')}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Informações Adicionais */}
                {draw.extra && (
                  <div className="mb-4 text-sm">
                    {draw.lottery_type === 'timemania' && draw.extra.time_coracao && (
                      <div className="flex justify-between">
                        <span className="text-white/60">Time do Coração:</span>
                        <span className="font-bold">{draw.extra.time_coracao}</span>
                      </div>
                    )}
                    {draw.lottery_type === 'dia-de-sorte' && draw.extra.mes_sorte && (
                      <div className="flex justify-between">
                        <span className="text-white/60">Mês da Sorte:</span>
                        <span className="font-bold">{draw.extra.mes_sorte}</span>
                      </div>
                    )}
                    {draw.lottery_type === 'federal' && draw.extra.bilhete && (
                      <div className="flex justify-between">
                        <span className="text-white/60">Bilhete:</span>
                        <span className="font-bold">{draw.extra.bilhete.toString().padStart(5, '0')}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Ações */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleCheckGames(draw)}
                    className="px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:shadow-lg transition-all text-sm font-medium"
                  >
                    <i className="ri-check-line mr-1"></i>
                    Conferir Jogos
                  </button>
                  <button
                    onClick={() => setSelectedDraw(draw)}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all text-sm font-medium"
                  >
                    <i className="ri-eye-line mr-1"></i>
                    Ver Detalhes
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedDraw && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold">{lotteryNames[selectedDraw.lottery_type as keyof typeof lotteryNames] || selectedDraw.lottery_type}</h3>
                  <p className="text-white/60 text-sm">Concurso {(selectedDraw as any).contest_number ?? selectedDraw.draw_number}</p>
                </div>
                <button onClick={() => setSelectedDraw(null)} className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10">Fechar</button>
              </div>
              <div className="mb-4">
                <p className="text-sm font-medium mb-2">Números Sorteados:</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {((selectedDraw as any).drawn_numbers ?? (selectedDraw as any).numbers ?? []).map((num: number, idx: number) => (
                    <div key={idx} className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg text-sm font-bold">
                      {num.toString().padStart(2, '0')}
                    </div>
                  ))}
                </div>
              </div>
              {selectedDraw.extra && (
                <pre className="text-xs bg-white/5 border border-white/10 rounded-lg p-3 overflow-auto max-h-48">
                  {JSON.stringify(selectedDraw.extra, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}

        {/* modal de preview removido */}

      </div>
    </div>
  );
}
